// Analytics dashboard page (MIS-813 / Task 32; FF-28 / MIS-1011) — FRONTEND.
//
// Per-tenant aggregate views over the REAL worker analytics endpoints
// (apps/worker/src/routes/analytics.ts, mounted at /api/analytics):
//   • GET /api/analytics/funnel       → { funnel: { visits, leads, calls,
//       conversions, revenueCents }, meta }       (FunnelCards tab)
//   • GET /api/analytics/by-utm      → { rows: [{ source, campaign, visits,
//       leads, over20k, over10k, below10k }], meta } (AttributionTable tab)
//   • GET /api/analytics/by-campaign → { rows: ByCampaignRow[], meta }
//       (FF-12; CampaignsTable tab — per fb_campaign_id incl. spend/ROAS)
//   • GET /api/analytics/overview    → { totals, byDomain, meta }
//       (FF-12; Overview tab — tenant totals cards + per-domain rollup with the
//       fb_costs vs creative_metrics spend SPLIT surfaced in a tooltip and the
//       additive combined spend as the primary column)
// All accept optional ET-day window params `from`/`to` (YYYY-MM-DD); funnel and
// by-campaign also accept `domainId`/`landerId`, by-utm `domainId`; overview is
// tenant-wide (window only). There is NO `/profit` endpoint in the shipped
// backend, so the Profit tab / ProfitChart are omitted. The new tabs keep the
// response `meta` and render its ET-day window label under the data
// (components/analytics/EtWindowLabel); the by-campaign meta's `scope` flags
// make the label state that calls/revenue/spend are all-time per campaign
// (QF-1 / MIS-1020 — only visits/leads honor the window there).
//
// The page owns the range presets (today/yesterday/7d/30d/custom) and converts
// each to a concrete ET-day window before querying (see lib/analytics). Each tab
// has its own React Query keyed by tenant + range + filters; fetch errors raise
// a toast. Filters: domain dropdown (from /api/domains) + lander dropdown (from
// /api/landers, scoped to the chosen domain). Analytics are AGGREGATES only —
// individual lead PII lives on the leads page, never here.

import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import type { InferResponseType } from 'hono/client';
import { useEffect, useMemo, useState } from 'react';
import { AttributionTable } from '@/components/analytics/AttributionTable';
import { CampaignsTable, formatRoas } from '@/components/analytics/CampaignsTable';
import { EtWindowLabel } from '@/components/analytics/EtWindowLabel';
import { FunnelCards } from '@/components/analytics/FunnelCards';
import { Card, CardContent } from '@/components/ui/Card';
import { DataTable } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Select } from '@/components/ui/Select';
import { useToast } from '@/components/ui/toast';
import {
  type DateRange,
  formatCount,
  formatCurrency,
  formatRange,
  presetToRange,
  type RangePreset,
} from '@/lib/analytics';
import type { ApiClient } from '@/lib/analytics-types';
import { useApi } from '@/lib/api';
import { useTenantContext } from '@/lib/auth';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_authenticated/analytics/')({
  component: AnalyticsPage,
});

type Tab = 'funnel' | 'attribution' | 'campaigns' | 'overview';

// Overview response, inferred from the typed RPC client (FF-12 contract).
// `InferResponseType` is the UNION of every status body; narrow to the success
// member by the discriminating `byDomain` key.
type OverviewResponse = InferResponseType<ApiClient['api']['analytics']['overview']['$get']>;
type OverviewSuccess = Extract<OverviewResponse, { byDomain: unknown }>;
type OverviewTotals = OverviewSuccess['totals'];
type OverviewDomainRow = OverviewSuccess['byDomain'][number];

// QF-11: the Overview endpoint accepts NO domainId/landerId (tenant-wide by
// design) — the filters are disabled on that tab with this explanation rather
// than silently ignored. Honoring them would be a worker contract change.
const OVERVIEW_FILTER_NOTE =
  'Overview is tenant-wide — the table below is the per-domain breakdown.';

const RANGE_OPTIONS: { value: RangePreset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'custom', label: 'Custom' },
];

/** Build the `from`/`to` query fragment, dropping undefined edges. */
function rangeQuery(range: DateRange | undefined): { from?: string; to?: string } {
  if (!range?.from || !range?.to) return {};
  return { from: range.from, to: range.to };
}

function CardSkeleton(): React.ReactElement {
  return (
    <Card>
      <CardContent className="py-5">
        <div className="h-4 w-20 animate-pulse rounded bg-gray-200" />
        <div className="mt-2 h-7 w-16 animate-pulse rounded bg-gray-200" />
      </CardContent>
    </Card>
  );
}

function FunnelSkeleton(): React.ReactElement {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
      {[0, 1, 2, 3, 4].map((i) => (
        <CardSkeleton key={i} />
      ))}
    </div>
  );
}

function TableSkeleton(): React.ReactElement {
  return (
    <div className="space-y-2 rounded-lg border border-gray-200 bg-white p-4">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="h-8 w-full animate-pulse rounded bg-gray-100" />
      ))}
    </div>
  );
}

/** Tenant-wide totals header cards for the Overview tab (FunnelCards style). */
function OverviewTotalsCards({ totals }: { totals: OverviewTotals }): React.ReactElement {
  const cards: { label: string; value: string }[] = [
    { label: 'Visits', value: formatCount(totals.visits) },
    { label: 'Leads', value: formatCount(totals.leads) },
    { label: 'Calls', value: formatCount(totals.calls) },
    { label: 'Conversions', value: formatCount(totals.conversions) },
    { label: 'Revenue', value: formatCurrency(totals.revenueCents) },
    { label: 'Spend', value: formatCurrency(totals.spendCents) },
    { label: 'ROAS', value: formatRoas(totals.roas) },
  ];
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-7">
      {cards.map((c) => (
        <Card key={c.label}>
          <CardContent className="py-5">
            <div className="text-sm font-medium text-gray-500">{c.label}</div>
            <div className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">{c.value}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// Per-domain rollup columns for the Overview tab. The combined `spendCents`
// column is primary; the fb_costs vs creative_metrics SPLIT is never folded
// away — it surfaces in the cell's tooltip (native `title`).
const OVERVIEW_COLUMNS: ColumnDef<OverviewDomainRow, unknown>[] = [
  {
    accessorKey: 'host',
    header: 'Domain',
    cell: ({ row }) => <span className="font-medium text-gray-900">{row.original.host}</span>,
  },
  {
    accessorKey: 'visits',
    header: 'Visits',
    cell: ({ row }) => <span className="tabular-nums">{formatCount(row.original.visits)}</span>,
  },
  {
    accessorKey: 'leads',
    header: 'Leads',
    cell: ({ row }) => <span className="tabular-nums">{formatCount(row.original.leads)}</span>,
  },
  {
    accessorKey: 'calls',
    header: 'Calls',
    cell: ({ row }) => <span className="tabular-nums">{formatCount(row.original.calls)}</span>,
  },
  {
    accessorKey: 'conversions',
    header: 'Conv',
    cell: ({ row }) => (
      <span className="tabular-nums">{formatCount(row.original.conversions)}</span>
    ),
  },
  {
    accessorKey: 'revenueCents',
    header: 'Revenue',
    cell: ({ row }) => (
      <span className="tabular-nums">{formatCurrency(row.original.revenueCents)}</span>
    ),
  },
  {
    accessorKey: 'spendCents',
    header: 'Spend',
    cell: ({ row }) => (
      <span
        className="tabular-nums underline decoration-dotted underline-offset-2"
        title={`FB costs ${formatCurrency(row.original.spendFbCostsCents)} + creative ${formatCurrency(row.original.spendCreativeCents)}`}
      >
        {formatCurrency(row.original.spendCents)}
      </span>
    ),
  },
  {
    id: 'roas',
    header: 'ROAS',
    cell: ({ row }) => <span className="tabular-nums">{formatRoas(row.original.roas)}</span>,
  },
];

function AnalyticsPage(): React.ReactElement {
  const api = useApi();
  const { toast } = useToast();
  const { activeTenantId } = useTenantContext();

  const [tab, setTab] = useState<Tab>('funnel');
  const [preset, setPreset] = useState<RangePreset>('7d');
  const [customRange, setCustomRange] = useState<DateRange>({ from: '', to: '' });
  const [domainId, setDomainId] = useState<string>('');
  const [landerId, setLanderId] = useState<string>('');

  // Resolve the active preset to a concrete ET-day window. `custom` uses the two
  // date inputs (only applied once both are filled).
  const range = useMemo<DateRange | undefined>(() => {
    if (preset === 'custom') {
      return customRange.from && customRange.to ? customRange : undefined;
    }
    return presetToRange(preset);
  }, [preset, customRange]);

  // Common, JSON-stable key fragment so each tab refetches on any filter change.
  const filterKey = { range, domainId, landerId };

  // Domain + lander dropdown sources. Tenant-scoped by the X-Tenant-Id header.
  const domainsQuery = useQuery({
    queryKey: ['analytics-domains', activeTenantId],
    queryFn: async () => {
      const res = await api.api.domains.$get();
      if (!res.ok) throw new Error('Failed to load domains');
      return (await res.json()).domains;
    },
  });

  const landersQuery = useQuery({
    queryKey: ['analytics-landers', activeTenantId, domainId],
    queryFn: async () => {
      const res = await api.api.landers.$get({
        query: domainId ? { domainId } : {},
      });
      if (!res.ok) throw new Error('Failed to load landers');
      return (await res.json()).landers;
    },
  });

  const funnelQuery = useQuery({
    queryKey: ['analytics-funnel', activeTenantId, filterKey],
    enabled: tab === 'funnel',
    queryFn: async () => {
      const res = await api.api.analytics.funnel.$get({
        query: {
          ...rangeQuery(range),
          ...(domainId ? { domainId } : {}),
          ...(landerId ? { landerId } : {}),
        },
      });
      if (!res.ok) throw new Error(`Funnel request failed (${res.status})`);
      return (await res.json()).funnel;
    },
  });

  const attributionQuery = useQuery({
    queryKey: ['analytics-by-utm', activeTenantId, filterKey],
    enabled: tab === 'attribution',
    queryFn: async () => {
      const res = await api.api.analytics['by-utm'].$get({
        query: {
          ...rangeQuery(range),
          ...(domainId ? { domainId } : {}),
        },
      });
      if (!res.ok) throw new Error(`Attribution request failed (${res.status})`);
      return (await res.json()).rows;
    },
  });

  // FF-28: per-campaign breakdown. Keeps the full `{ rows, meta }` envelope so
  // the tab can render the meta ET-day window label.
  const campaignsQuery = useQuery({
    queryKey: ['analytics-by-campaign', activeTenantId, filterKey],
    enabled: tab === 'campaigns',
    queryFn: async () => {
      const res = await api.api.analytics['by-campaign'].$get({
        query: {
          ...rangeQuery(range),
          ...(domainId ? { domainId } : {}),
          ...(landerId ? { landerId } : {}),
        },
      });
      if (!res.ok) throw new Error(`Campaigns request failed (${res.status})`);
      return await res.json();
    },
  });

  // FF-28: tenant-wide overview — window-only by design (no domain/lander
  // narrowing; the per-domain rollup IS the domain breakdown).
  const overviewQuery = useQuery({
    queryKey: ['analytics-overview', activeTenantId, range],
    enabled: tab === 'overview',
    queryFn: async () => {
      const res = await api.api.analytics.overview.$get({
        query: { ...rangeQuery(range) },
      });
      if (!res.ok) throw new Error(`Overview request failed (${res.status})`);
      return await res.json();
    },
  });

  // Surface fetch failures as toasts. Keyed on the error message so the toast
  // fires once per distinct failure (not on every render while it persists).
  const funnelErrorMsg = funnelQuery.error?.message;
  const attributionErrorMsg = attributionQuery.error?.message;
  const campaignsErrorMsg = campaignsQuery.error?.message;
  const overviewErrorMsg = overviewQuery.error?.message;
  useEffect(() => {
    if (funnelErrorMsg) {
      toast({
        title: 'Could not load funnel',
        description: funnelErrorMsg,
        variant: 'destructive',
      });
    }
  }, [funnelErrorMsg, toast]);
  useEffect(() => {
    if (attributionErrorMsg) {
      toast({
        title: 'Could not load attribution',
        description: attributionErrorMsg,
        variant: 'destructive',
      });
    }
  }, [attributionErrorMsg, toast]);
  useEffect(() => {
    if (campaignsErrorMsg) {
      toast({
        title: 'Could not load campaigns',
        description: campaignsErrorMsg,
        variant: 'destructive',
      });
    }
  }, [campaignsErrorMsg, toast]);
  useEffect(() => {
    if (overviewErrorMsg) {
      toast({
        title: 'Could not load overview',
        description: overviewErrorMsg,
        variant: 'destructive',
      });
    }
  }, [overviewErrorMsg, toast]);

  const tabs: { id: Tab; label: string }[] = [
    { id: 'funnel', label: 'Funnel' },
    { id: 'attribution', label: 'Attribution' },
    { id: 'campaigns', label: 'Campaigns' },
    { id: 'overview', label: 'Overview' },
  ];

  return (
    <div className="space-y-6 p-8">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Analytics</h1>
        <p className="text-sm text-gray-500">
          Aggregate funnel and attribution for this workspace — {formatRange(preset, range)} (ET).
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-40">
          <Label htmlFor="range">Range</Label>
          <Select
            id="range"
            className="mt-1"
            value={preset}
            onChange={(e) => setPreset(e.target.value as RangePreset)}
          >
            {RANGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </div>

        {preset === 'custom' ? (
          <>
            <div className="w-40">
              <Label htmlFor="from">From</Label>
              <Input
                id="from"
                type="date"
                className="mt-1"
                value={customRange.from}
                onChange={(e) => setCustomRange((r) => ({ ...r, from: e.target.value }))}
              />
            </div>
            <div className="w-40">
              <Label htmlFor="to">To</Label>
              <Input
                id="to"
                type="date"
                className="mt-1"
                value={customRange.to}
                onChange={(e) => setCustomRange((r) => ({ ...r, to: e.target.value }))}
              />
            </div>
          </>
        ) : null}

        {/* QF-11: the Overview endpoint is tenant-wide by design (no
            domainId/landerId narrowing on the worker), so the two filters are
            DISABLED on that tab instead of silently doing nothing. Selections
            are kept (state untouched) and re-apply on switching back. */}
        <div className="w-48">
          <Label htmlFor="domain">Domain</Label>
          <Select
            id="domain"
            className="mt-1"
            value={domainId}
            disabled={tab === 'overview'}
            title={tab === 'overview' ? OVERVIEW_FILTER_NOTE : undefined}
            onChange={(e) => {
              setDomainId(e.target.value);
              // A domain change can invalidate the chosen lander; reset it.
              setLanderId('');
            }}
          >
            <option value="">All domains</option>
            {(domainsQuery.data ?? []).map((d) => (
              <option key={d.id} value={String(d.id)}>
                {d.host}
              </option>
            ))}
          </Select>
        </div>

        <div className="w-48">
          <Label htmlFor="lander">Lander</Label>
          <Select
            id="lander"
            className="mt-1"
            value={landerId}
            disabled={tab === 'overview'}
            title={tab === 'overview' ? OVERVIEW_FILTER_NOTE : undefined}
            onChange={(e) => setLanderId(e.target.value)}
          >
            <option value="">All landers</option>
            {(landersQuery.data ?? []).map((l) => (
              <option key={l.id} value={String(l.id)}>
                {l.slug}
              </option>
            ))}
          </Select>
        </div>

        {tab === 'overview' ? (
          <p className="w-full text-xs text-gray-500">{OVERVIEW_FILTER_NOTE}</p>
        ) : null}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                'border-b-2 px-1 py-2 text-sm font-medium transition-colors',
                tab === t.id
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700',
              )}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {tab === 'funnel' ? (
        funnelQuery.isLoading ? (
          <FunnelSkeleton />
        ) : funnelQuery.data ? (
          <FunnelCards funnel={funnelQuery.data} />
        ) : (
          <p className="text-sm text-gray-500">No funnel data for this range.</p>
        )
      ) : null}

      {tab === 'attribution' ? (
        attributionQuery.isLoading ? (
          <TableSkeleton />
        ) : (
          <AttributionTable rows={attributionQuery.data ?? []} />
        )
      ) : null}

      {tab === 'campaigns' ? (
        campaignsQuery.isLoading ? (
          <TableSkeleton />
        ) : (
          <div className="space-y-3">
            <CampaignsTable rows={campaignsQuery.data?.rows ?? []} />
            {campaignsQuery.data ? <EtWindowLabel meta={campaignsQuery.data.meta} /> : null}
          </div>
        )
      ) : null}

      {tab === 'overview' ? (
        overviewQuery.isLoading ? (
          <div className="space-y-6">
            <FunnelSkeleton />
            <TableSkeleton />
          </div>
        ) : overviewQuery.data ? (
          <div className="space-y-6">
            <OverviewTotalsCards totals={overviewQuery.data.totals} />
            <DataTable
              columns={OVERVIEW_COLUMNS}
              data={overviewQuery.data.byDomain}
              pageSize={20}
              emptyMessage="No domain data for this range yet."
            />
            <EtWindowLabel meta={overviewQuery.data.meta} />
          </div>
        ) : (
          <p className="text-sm text-gray-500">No overview data for this range.</p>
        )
      ) : null}
    </div>
  );
}
